import React, { useState, useRef, useMemo, useEffect } from "react";
import { Upload, X, BookOpen } from "lucide-react";
import JoditEditor from "jodit-react";
import { toast } from "react-toastify";
import styles from "./AddEbook.module.css";
import { useCategories } from "../../../hooks/categories/useCategories";
import { useEbook, useUpdateEbook } from "../../../hooks/admin/useAdminEbooks";
import Loading from "../../../common/HousGlass Loader/Loading";
import { useNavigate, useParams } from "react-router-dom";

const EditEbook = () => {
  const { id } = useParams();
  const [formData, setFormData] = useState({
    title: "",
    author: "",
    description: "",
    mobileImage: null,
    desktopImage: null,
    content: "",
    hasChapters: false,
    genreId: "",
    genreName: "",
    language: "english", // Add this line
    removeMobileImage: false, // Add this line
    removeDesktopImage: false,
    slug:''
  });

  const [mobileImagePreview, setMobileImagePreview] = useState("");
  const [desktopImagePreview, setDesktopImagePreview] = useState("");
  const editor = useRef(null);
  const { categories = [] } = useCategories("Ebook");
  const { ebook, isLoading: isLoadingEbook } = useEbook(id);
  const { updateEbook, isLoading: isUpdating } = useUpdateEbook();
  const navigate = useNavigate();

  useEffect(() => {
    if (ebook) {
      setFormData({
        title: ebook.title || "",
        author: ebook.author || "",
        description: ebook.description || "",
        content: ebook.full_content || "",
        hasChapters: ebook.has_chapters || false,
        genreId: ebook.genre_id || "",
        mobileImage: null,
        desktopImage: null,
        language: ebook.language || "",
        slug:ebook.slug || ""
      });

      // Set image previews from existing URLs
      if (ebook.cover_image_url_for_mbl) {
        setMobileImagePreview(ebook.cover_image_url_for_mbl);
      }
      if (ebook.cover_image_url_for_computer) {
        setDesktopImagePreview(ebook.cover_image_url_for_computer);
      }
    }
  }, [ebook]);

  const joditConfig = useMemo(
    () => ({
      theme: "dark",
      height: 600,
      enableDragAndDropFileToEditor: true,
      showXPathInStatusbar: false,
      buttons: [
        "source",
        "|",
        "bold",
        "italic",
        "underline",
        "strikethrough",
        "|",
        "font",
        "fontsize",
        "brush",
        "paragraph",
        "|",
        "ul",
        "ol",
        "|",
        "table",
        "link",
        "|",
        "left",
        "center",
        "right",
        "justify",
        "|",
        "undo",
        "redo",
        "|",
        "hr",
        "eraser",
        "fullsize",
      ],
      uploader: {
        insertImageAsBase64URI: true,
      },
      colors: {
        background: "#222222",
        text: "#ffffff",
        defaultTextColor: "#ffffff",
        defaultBackgroundColor: "#222222",
      },
      style: {
        background: "#222222",
        color: "#ffffff",
      },
      statusbar: false,
      readonly: false,
      toolbarSticky: true,
      showPlaceholder: false,
      saveIndicator: false,
      maxHeight: 800,
      minHeight: 400,
      useSearch: false,
      spellcheck: false,
      editorCssClass: "dark-theme",
    }),
    []
  );

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'genreId') {
      // Find the selected genre to get its name
      const selectedGenre = categories.find(genre => genre.id === value);
      setFormData(prev => ({
        ...prev,
        [name]: value,
        genreName: selectedGenre ? selectedGenre.name : ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleImageUpload = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === "mobile") {
          setMobileImagePreview(reader.result);
          setFormData((prev) => ({ ...prev, mobileImage: file }));
        } else {
          setDesktopImagePreview(reader.result);
          setFormData((prev) => ({ ...prev, desktopImage: file }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = (type) => {
    if (type === "mobile") {
      setMobileImagePreview(""); // Set to empty string instead of falling back to original
      setFormData((prev) => ({
        ...prev,
        mobileImage: null,
        // Add a flag to indicate the image should be removed from the server
        removeMobileImage: true,
      }));
    } else {
      setDesktopImagePreview(""); // Set to empty string instead of falling back to original
      setFormData((prev) => ({
        ...prev,
        desktopImage: null,
        // Add a flag to indicate the image should be removed from the server
        removeDesktopImage: true,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await updateEbook({ id, formData });
    navigate("/admin/dashboard");
  };

  if (isLoadingEbook || isUpdating) {
    return (
      <div>
        <Loading />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <BookOpen className={styles.headerIcon} />
        <h1>Edit Ebook</h1>
      </div>

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formGroup}>
          <label htmlFor="title">Title</label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className={styles.formGroup}>
          <label htmlFor="slug">Slug</label>
          <input
            type="text"
            id="slug"
            name="slug"
            value={formData.slug}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="author">Author</label>
          <input
            type="text"
            id="author"
            name="author"
            value={formData.author}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="genreId">Genre</label>
          <select
            id="genreId"
            name="genreId"
            value={formData.genreId}
            onChange={handleInputChange}
            required
            className={styles.select}
          >
            <option value="">Select a genre</option>
            {categories.map((genre) => (
              <option key={genre.id} value={genre.id}>
                {genre.name}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="language">Language</label>
          <select
            id="language"
            name="language"
            value={formData.language}
            onChange={handleInputChange}
            required
            className={styles.select}
          >
            <option value="english">English</option>
            <option value="spanish">Spanish</option>
            <option value="garhwali">Garhwali</option>
            <option value="hindi">Hindi</option>
            <option value="french">Hindi</option>
            <option value="german">German</option>
            <option value="chinese">Chinese</option>
            <option value="japanese">Japanese</option>
            <option value="korean">Garhwali</option>
            <option value="arabic">Kumaoni</option>
          </select>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows="4"
          />
        </div>

        <div className={styles.imageUploadSection}>
          <div className={styles.imageUpload}>
            <label>Mobile Cover Image</label>
            <div className={styles.uploadArea}>
              {mobileImagePreview ? (
                <div className={styles.imagePreview}>
                  <img src={mobileImagePreview} alt="Mobile cover preview" />
                  <button
                    type="button"
                    onClick={() => removeImage("mobile")}
                    className={styles.removeButton}
                  >
                    <X />
                  </button>
                </div>
              ) : (
                <div className={styles.uploadPlaceholder}>
                  <input
                    type="file"
                    id="mobileImage"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, "mobile")}
                    className={styles.fileInput}
                  />
                  <Upload />
                  <span>Upload Mobile Cover</span>
                </div>
              )}
            </div>
          </div>

          <div className={styles.imageUpload}>
            <label>Desktop Cover Image</label>
            <div className={styles.uploadArea}>
              {desktopImagePreview ? (
                <div className={styles.imagePreview}>
                  <img src={desktopImagePreview} alt="Desktop cover preview" />
                  <button
                    type="button"
                    onClick={() => removeImage("desktop")}
                    className={styles.removeButton}
                  >
                    <X />
                  </button>
                </div>
              ) : (
                <div className={styles.uploadPlaceholder}>
                  <input
                    type="file"
                    id="desktopImage"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, "desktop")}
                    className={styles.fileInput}
                  />
                  <Upload />
                  <span>Upload Desktop Cover</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="content">Content</label>
          <JoditEditor
            ref={editor}
            value={formData.content}
            config={joditConfig}
            onChange={(newContent) =>
              setFormData((prev) => ({ ...prev, content: newContent }))
            }
          />
        </div>

        <button type="submit" className={styles.submitButton}>
          Update Ebook
        </button>
      </form>
    </div>
  );
};

export default EditEbook;
